import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useIsPersonalInformationFieldLocked } from '@/features/onboarding/personal-information/hooks/use-is-field-locked';
import type { PersonalInformationAboutYou } from '@/features/onboarding/personal-information/schema';
import { getMonthNames } from '@/utils/date';

type DateOfBirthFieldKey = keyof PersonalInformationAboutYou['dateOfBirth'];

const DATE_OF_BIRTH_FIELDS = ['day', 'month', 'year'] satisfies DateOfBirthFieldKey[];

const PersonalInformationFormPersonalDetailsFieldsetDateOfBirth = () => {
  const form = useFormContext<PersonalInformationAboutYou>();
  const isLocked = useIsPersonalInformationFieldLocked();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="dateOfBirth"
      render={({ fieldState: { error } }) => {
        const rootError = error?.message;
        const hasFieldErrors = error && Object.keys(error).some((key) => DATE_OF_BIRTH_FIELDS.includes(key as DateOfBirthFieldKey));

        return (
          <FormItem className="col-span-full">
            <FormControl>
              <fieldset>
                <FormLabel asChild>
                  <legend>{t('personalInformation.dateOfBirth')}*</legend>
                </FormLabel>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="dateOfBirth.day"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.day')}</FormLabel>
                        <FormControl>
                          <Input {...field} className="no-spinner" disabled={isLocked('dateOfBirth.day')} placeholder={t('fundamentals.date.day')} type="number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dateOfBirth.month"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.month')}</FormLabel>
                        <Select defaultValue={field.value} disabled={isLocked('dateOfBirth.month')} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger value={field.value}>
                              <SelectValue placeholder={t('fundamentals.date.month')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {getMonthNames().map((month, index) => (
                              <SelectItem key={month} value={(index + 1).toString()}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dateOfBirth.year"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel className="sr-only">{t('fundamentals.date.year')}</FormLabel>
                        <FormControl>
                          <Input {...field} className="no-spinner" disabled={isLocked('dateOfBirth.year')} placeholder={t('fundamentals.date.year')} type="number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </fieldset>
            </FormControl>
            {rootError && !hasFieldErrors ? <FormMessage>{rootError}</FormMessage> : null}
          </FormItem>
        );
      }}
    />
  );
};
PersonalInformationFormPersonalDetailsFieldsetDateOfBirth.displayName = 'PersonalInformationFormPersonalDetailsFieldsetDateOfBirth';

export default PersonalInformationFormPersonalDetailsFieldsetDateOfBirth;
